import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { useIonRouter } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { ProfileWizardLogic } from '@/modules/profile/logic';
import type { ProfileWizardStep } from '@/modules/profile/logic';
import { RETURN_BUTTON_LABEL_PARAM } from '@/modules/profile/logic';

export type { ProfileWizardStep } from '@/modules/profile/logic';

// Blur the active element (drilling through shadow roots, since Ionic inputs
// host their real field in a shadow DOM) so focus and the on-screen keyboard
// don't bleed across the Ionic page transition into the next step.
function blurActiveElement(): void {
  if (typeof document === 'undefined') return;
  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLElement)) return;
  blurShadowActiveElement(activeElement);
  activeElement.blur();
}

function blurShadowActiveElement(element: HTMLElement): void {
  const activeElement = element.shadowRoot?.activeElement;
  if (!(activeElement instanceof HTMLElement)) return;
  blurShadowActiveElement(activeElement);
  activeElement.blur();
}

interface ProfileWizardContextType {
  // Form state. Attribution (names + pronouns) is NOT held here — it
  // write-throughs to UserContext as it is edited (see AttributionStep), so the
  // wizard reads/writes it via UserContext directly. `phone`/`email` stay
  // wizard-scoped: they hold raw, possibly-invalid in-progress input that must
  // NOT reach the committed/persisted UserContext until verified (the #414/#419
  // pending model).
  phone: string;
  setPhone: (phone: string) => void;
  email: string;
  setEmail: (email: string) => void;
  // Snapshot of the value when the wizard opened, for change detection
  // (isVerifyingNewNumber / isEmailEdited). UserContext has no equivalent.
  originalPhone: string;
  setOriginalPhone: (phone: string) => void;
  originalEmail: string;
  setOriginalEmail: (email: string) => void;

  // Verification modal state
  showPhoneModal: boolean;
  setShowPhoneModal: (show: boolean) => void;
  showEmailModal: boolean;
  setShowEmailModal: (show: boolean) => void;

  // Navigation
  urlProvidedOrigin: string | undefined;
  routerProvidedOrigin: string | undefined;
  navigateWithOrigin: (
    path: string,
    direction?: 'forward' | 'back' | 'none'
  ) => void;
  // The step named by the current URL, and its progress-bar value (0–1). Steps
  // read these to drive the shared header, mirroring the Share Wizard.
  currentStep: ProfileWizardStep;
  progress: number;
  // Navigate to a step by identity (blurring focus first, like the Share
  // Wizard). Back handlers use this with the step's declared Back target;
  // conditional forward jumps still use navigateWithOrigin with explicit paths.
  navigateToStep: (
    step: ProfileWizardStep,
    direction?: 'forward' | 'back' | 'none'
  ) => void;
  exitWizard: () => Promise<void>;
}

const ProfileWizardContext = createContext<
  ProfileWizardContextType | undefined
>(undefined);

export const ProfileWizardProvider: React.FC<{
  children: ReactNode;
  refreshUserContext?: () => Promise<unknown>;
}> = ({ children, refreshUserContext }) => {
  const router = useIonRouter();
  const location = useLocation();

  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [originalPhone, setOriginalPhone] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Capture router-provided origin from routeOptions on first render and retain it
  // routeOptions is only available on initial navigation, so we capture it once
  const [routerProvidedOrigin, setRouterProvidedOrigin] = useState<
    string | undefined
  >(() => {
    const state = router.routeInfo?.routeOptions as
      | { from?: string }
      | null
      | undefined;
    const from = state?.from;
    if (from && from !== 'undefined' && from.trim() !== '') {
      return from;
    }
    return undefined;
  });

  // Capture URL-provided origin from query params on first render and retain it
  // This is for IDV-initiated tab scenarios where origin comes from URL query params
  const [urlProvidedOrigin, setUrlProvidedOrigin] = useState<
    string | undefined
  >(() => {
    const queryString = router.routeInfo?.search || location.search;
    const queryParams = new URLSearchParams(queryString);
    const origin = queryParams.get('origin');
    if (origin) {
      return origin;
    }
    return undefined;
  });

  // Update both origins if they become available and we don't have values yet
  useEffect(() => {
    // Update routerProvidedOrigin if routeOptions changes and we don't have a value yet
    if (!routerProvidedOrigin) {
      const state = router.routeInfo?.routeOptions as
        | { from?: string }
        | null
        | undefined;
      const from = state?.from;
      if (from && from !== 'undefined' && from.trim() !== '') {
        setRouterProvidedOrigin(from);
      }
    }

    // Update urlProvidedOrigin if query params change and we don't have a value yet
    if (!urlProvidedOrigin) {
      const queryString = router.routeInfo?.search || location.search;
      const queryParams = new URLSearchParams(queryString);
      const origin = queryParams.get('origin');
      if (origin) {
        setUrlProvidedOrigin(origin);
      }
    }
  }, [
    router.routeInfo?.routeOptions,
    router.routeInfo?.search,
    location.search,
    routerProvidedOrigin,
    urlProvidedOrigin,
  ]);

  // Helper function to navigate with origin query param preserved. The current
  // step's `returnButtonLabel` rides along too, so a caller's way back (see
  // AttributionStep) survives the user stepping back and forth through the
  // wizard; each step replaces the last, so goBack still lands on that caller.
  const navigateWithOrigin = (
    path: string,
    direction: 'forward' | 'back' | 'none' = 'forward'
  ) => {
    const params = new URLSearchParams();
    if (urlProvidedOrigin) params.set('origin', urlProvidedOrigin);
    const returnButtonLabel = new URLSearchParams(
      router.routeInfo?.search || location.search
    ).get(RETURN_BUTTON_LABEL_PARAM);
    if (returnButtonLabel) {
      params.set(RETURN_BUTTON_LABEL_PARAM, returnButtonLabel);
    }
    const search = params.toString();
    router.push(search ? `${path}?${search}` : path, direction, 'replace');
  };

  // The current step (and its progress) derived from the URL, so the shared
  // header reflects wherever the router lands without any per-step wiring.
  const currentStep = ProfileWizardLogic.stepFromPath(
    router.routeInfo?.pathname || location.pathname || ''
  );
  const progress = ProfileWizardLogic.progressForStep(currentStep);

  // Navigate to a step by identity. Blurs focus first (so the keyboard doesn't
  // bleed across the page transition) then reuses navigateWithOrigin so the
  // origin query param is preserved exactly as elsewhere.
  const navigateToStep = (
    step: ProfileWizardStep,
    direction: 'forward' | 'back' | 'none' = 'forward'
  ) => {
    blurActiveElement();
    navigateWithOrigin(ProfileWizardLogic.pathForStep(step), direction);
  };

  const exitWizard = async () => {
    // Refresh UserContext to sync changes made during the wizard
    // This ensures name, phone, email, and trust level are up-to-date
    if (refreshUserContext) {
      refreshUserContext().catch(error => {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to refresh user context after wizard exit:',
          error
        );
      });
    }

    // If urlProvidedOrigin exists, use it (for IDV-initiated tab scenarios)
    // Fall back to router.goBack() otherwise
    if (urlProvidedOrigin) {
      router.push(urlProvidedOrigin, 'back', 'replace');
    } else {
      router.goBack();
    }
  };

  const value = {
    phone,
    setPhone,
    email,
    setEmail,
    originalPhone,
    setOriginalPhone,
    originalEmail,
    setOriginalEmail,
    showPhoneModal,
    setShowPhoneModal,
    showEmailModal,
    setShowEmailModal,
    urlProvidedOrigin,
    routerProvidedOrigin,
    navigateWithOrigin,
    currentStep,
    progress,
    navigateToStep,
    exitWizard,
  };

  return (
    <ProfileWizardContext.Provider value={value}>
      {children}
    </ProfileWizardContext.Provider>
  );
};

export const useProfileWizardContext = () => {
  const context = useContext(ProfileWizardContext);
  if (context === undefined) {
    throw new Error(
      'useProfileWizardContext must be used within a ProfileWizardProvider'
    );
  }
  return context;
};
